import * as THREE from 'three';
import { EFFECT_LAYER } from '../renderer/enhanced-effects';
import { isMobile } from '../core/device';

const MAX_ACTIVE = 20;
const LIFETIME = 0.8; // seconds
const FLOAT_DIST = 0.5;
const CANVAS_W = 128;
const CANVAS_H = 64;

export type DamageType = 'headshot' | 'body' | 'leg';

interface DamageNumber {
  sprite: THREE.Sprite;
  age: number;
  startY: number;
}

function getDamageColor(type: DamageType): string {
  switch (type) {
    case 'headshot':
      return '#ffdd00';
    case 'leg':
      return '#8fd3ff';
    case 'body':
    default:
      return '#ffffff';
  }
}

function createTexture(damage: number, isHeadshot: boolean, damageType?: DamageType): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d')!;

  const resolvedType = damageType ?? (isHeadshot ? 'headshot' : 'body');
  const headshot = resolvedType === 'headshot';
  const text = headshot ? `${damage}!` : `${damage}`;
  const fontSize = headshot ? 42 : 36;

  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Black stroke outline
  ctx.strokeStyle = '#000000';
  ctx.lineWidth = 4;
  ctx.strokeText(text, CANVAS_W / 2, CANVAS_H / 2);

  // Fill
  ctx.fillStyle = getDamageColor(resolvedType);
  ctx.fillText(text, CANVAS_W / 2, CANVAS_H / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export class DamageNumberSystem {
  private scene: THREE.Scene;
  private active: DamageNumber[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawn(position: THREE.Vector3, damage: number, isHeadshot: boolean, damageType?: DamageType): void {
    // Recycle oldest if at capacity
    if (this.active.length >= MAX_ACTIVE) {
      const oldest = this.active.shift()!;
      this.scene.remove(oldest.sprite);
      oldest.sprite.material.map?.dispose();
      oldest.sprite.material.dispose();
    }

    const resolvedType = damageType ?? (isHeadshot ? 'headshot' : 'body');
    const texture = createTexture(damage, isHeadshot, resolvedType);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });

    const sprite = new THREE.Sprite(material);
    const baseSize = resolvedType === 'headshot' ? 0.25 : 0.15;
    const size = isMobile() ? baseSize * 1.6 : baseSize;
    sprite.scale.set(size * (CANVAS_W / CANVAS_H), size, 1);
    sprite.position.copy(position);
    sprite.layers.set(EFFECT_LAYER);

    this.scene.add(sprite);
    this.active.push({
      sprite,
      age: 0,
      startY: position.y,
    });
  }

  update(dt: number, _camera: THREE.Camera): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const dn = this.active[i];
      dn.age += dt;

      if (dn.age >= LIFETIME) {
        this.scene.remove(dn.sprite);
        dn.sprite.material.map?.dispose();
        dn.sprite.material.dispose();
        this.active.splice(i, 1);
        continue;
      }

      const t = dn.age / LIFETIME;
      // Float up
      dn.sprite.position.y = dn.startY + t * FLOAT_DIST;
      // Fade out
      (dn.sprite.material as THREE.SpriteMaterial).opacity = 1 - t;
    }
  }

  dispose(): void {
    for (const dn of this.active) {
      this.scene.remove(dn.sprite);
      dn.sprite.material.map?.dispose();
      dn.sprite.material.dispose();
    }
    this.active.length = 0;
  }
}
